import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import Icon from '../components/Icon';

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 172800) return 'Yesterday';
  return new Date(dateStr).toLocaleDateString();
}

export default function Messages() {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [team, setTeam] = useState([]);
  const [composeForm, setComposeForm] = useState({ recipient_id: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/messages/inbox').catch(() => []),
      api.get('/messages/sent').catch(() => []),
      api.get('/settings/team').catch(() => []),
    ]).then(([i, s, t]) => {
      setInbox(i);
      setSent(s);
      setTeam(t.filter(m => m.id !== trainer?.id));
    }).finally(() => setLoading(false));
  }, [trainer]);

  function openMessage(msg) {
    setSelected(msg);
    if (!msg.is_read && tab === 'inbox') {
      api.put('/messages/' + msg.id + '/read').catch(() => {});
      setInbox(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
    }
  }

  async function handleSend(e) {
    e.preventDefault();
    setSending(true);
    try {
      const msg = await api.post('/messages', composeForm);
      setSent(prev => [{ ...msg, recipient_name: team.find(t => String(t.id) === String(composeForm.recipient_id))?.name }, ...prev]);
      setShowCompose(false);
      setComposeForm({ recipient_id: '', subject: '', body: '' });
      setTab('sent');
    } catch (err) {
      alert(err.message);
    } finally {
      setSending(false);
    }
  }

  const messages = tab === 'inbox' ? inbox : sent;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header">
        <h1>
          <button className="icon-btn" style={{ marginRight: 4 }} onClick={() => navigate(-1)}>
            <span style={{ fontSize: 22, color: 'var(--color-text)' }}>‹</span>
          </button>
          Messages
        </h1>
        <button className="icon-btn" onClick={() => { setShowCompose(v => !v); setSelected(null); }} title="Compose">
          <Icon name="icon-add.png" alt="Compose" />
        </button>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
        {[['inbox', 'Inbox'], ['sent', 'Sent']].map(([key, label]) => (
          <button key={key} onClick={() => { setTab(key); setSelected(null); }}
            style={{ flex: 1, padding: '12px', background: 'transparent', border: 'none', borderBottom: tab === key ? '2px solid var(--color-primary)' : '2px solid transparent', color: tab === key ? 'var(--color-primary)' : 'var(--color-text-muted)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {label}
            {key === 'inbox' && inbox.filter(m => !m.is_read).length > 0 && (
              <span style={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>
                {inbox.filter(m => !m.is_read).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="page-content">
        {/* Compose form */}
        {showCompose && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>New Message</div>
            <form onSubmit={handleSend}>
              <div className="form-group">
                <label className="form-label">To</label>
                <select className="form-select" value={composeForm.recipient_id} onChange={e => setComposeForm(f => ({ ...f, recipient_id: e.target.value }))} required>
                  <option value="">Select team member</option>
                  {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <input className="form-input" placeholder="Optional" value={composeForm.subject} onChange={e => setComposeForm(f => ({ ...f, subject: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Message *</label>
                <textarea className="form-textarea" value={composeForm.body} onChange={e => setComposeForm(f => ({ ...f, body: e.target.value }))} required placeholder="Write your message..." style={{ minHeight: 100 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={sending} style={{ flex: 1 }}>{sending ? 'Sending...' : 'Send'}</button>
                <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setShowCompose(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Selected message */}
        {selected && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{tab === 'inbox' ? selected.sender_name : selected.recipient_name}</div>
                {selected.subject && <div style={{ fontSize: 14, color: 'var(--color-text-muted)', marginTop: 2 }}>{selected.subject}</div>}
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, fontSize: 14, lineHeight: 1.6, color: 'var(--color-text)', whiteSpace: 'pre-wrap' }}>{selected.body}</div>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--color-text-muted)' }}>{new Date(selected.created_at).toLocaleString()}</div>
          </div>
        )}

        {loading ? (
          <div className="loading-spinner"><span className="spinner" /></div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <Icon name="icon-notification.png" size={48} style={{ opacity: 0.3 }} />
            <p>{tab === 'inbox' ? 'No messages in your inbox' : 'No sent messages'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.map(msg => (
              <button key={msg.id} onClick={() => openMessage(msg)}
                style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                {tab === 'inbox' && !msg.is_read && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-primary)', flexShrink: 0, marginTop: 5 }} />
                )}
                <div style={{ flex: 1, minWidth: 0, marginLeft: tab === 'inbox' && msg.is_read ? 20 : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: tab === 'inbox' && !msg.is_read ? 700 : 600, fontSize: 14 }}>
                      {tab === 'inbox' ? msg.sender_name : msg.recipient_name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-muted)', flexShrink: 0 }}>{timeAgo(msg.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {msg.subject || msg.body.substring(0, 60)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
